import { HOUSE_DESCRIPTION_BUDGET, HOUSE_STANDARD_NUMBER } from '../config/scope-writing-contract.js';

/**
 * Structural shape of a scope item w.r.t. client-facing copy fields. All
 * fields optional; the index signature keeps it permissive so callers passing
 * full scope items don't need to narrow. Values are loosely typed because the
 * helpers defensively coerce.
 */
export interface ScopeCopyItem {
    title?: unknown;
    description?: unknown;
    descriptionBrief?: unknown;
    description_brief?: unknown;
    clientDescription?: unknown;
    notes_to_client?: unknown;
    inclusions?: unknown;
    manuallyEdited?: unknown;
    editedBy?: unknown;
    humanEditedAt?: unknown;
    [key: string]: unknown;
}

/** Options for resolving a client-facing scope description. */
export interface ResolveClientScopeOptions {
    fallback?: ScopeCopyItem;
    limit?: number | null;
    fallbackToTitle?: boolean;
}

function cleanText(value: unknown): string {
    return normaliseQuotePunctuation(String(value || ''))
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function cleanDisplayText(value: unknown): string {
    return normaliseQuotePunctuation(String(value || ''))
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
        .replace(/<\/?(p|div|li|ul|ol)[^>]*>/gi, '\n')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\r\n?/g, '\n')
        .replace(/[ \t\f\v]+/g, ' ')
        .replace(/[ \t]*\n[ \t]*/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

export function normaliseQuotePunctuation(value: unknown): string {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/[ \t]*[\u2012\u2013\u2014\u2015][ \t]*/g, ' - ')
        .replace(/[ \t]{2,}/g, ' ');
}

export function normaliseScopeCopyItem<T>(item: T = {} as T): T {
    if (!item || typeof item !== 'object') return item;
    const next = { ...item } as ScopeCopyItem;
    [
        'title',
        'description',
        'descriptionBrief',
        'description_brief',
        'clientDescription',
        'notes_to_client',
    ].forEach((field) => {
        if (typeof next[field] === 'string') next[field] = normaliseQuotePunctuation(next[field]).trim();
    });
    if (Array.isArray(next.inclusions)) {
        next.inclusions = next.inclusions.map((inc) => {
            if (typeof inc === 'string') return normaliseQuotePunctuation(inc).trim();
            if (inc && typeof inc === 'object' && typeof (inc as { text?: unknown }).text === 'string') {
                return { ...inc, text: normaliseQuotePunctuation((inc as { text?: unknown }).text).trim() };
            }
            return inc;
        });
    }
    return next as T;
}

function finitePositiveLimit(value: unknown): number | null {
    return Number.isFinite(value) && (value as number) > 0 ? (value as number) : null;
}

function limitTextAtWordBoundary(value: unknown, limit: number | null | undefined): string {
    const text = String(value || '');
    const max = finitePositiveLimit(limit);
    if (!max || text.length <= max) return text;
    const bodyMax = Math.max(0, max - 3);
    const clipped = text.slice(0, bodyMax);
    const lastSpace = clipped.lastIndexOf(' ');
    const safe = lastSpace > Math.max(20, Math.floor(bodyMax * 0.6))
        ? clipped.slice(0, lastSpace)
        : clipped;
    return `${safe.trim()}...`;
}

export function firstSentence(value: unknown, limit: number | null = null): string {
    const text = cleanText(value);
    if (!text) return '';
    const match = text.match(/^(.{1,220}?[.!?])(?:\s|$)/);
    // Capture group 1 is provably defined when `match` is truthy (the pattern
    // requires `.{1,220}?[.!?]`). The `!` keeps runtime byte-identical while
    // satisfying noUncheckedIndexedAccess.
    const sentence = match ? match[1]!.trim() : text;
    return limitTextAtWordBoundary(sentence, limit);
}

export function isWeakClientScopeDescription(value: unknown): boolean {
    const text = cleanText(value).toLowerCase();
    if (!text) return true;
    const genericPatterns = [
        /\bprotect your home\b/,
        /\bmanage dust\b/,
        /\bminimi[sz]e disruption\b/,
        /\brespect your home\b/,
        /\bkeep (the )?site clean\b/,
        /\bclean and tidy\b/,
        /\bwork area safe\b/,
        /\bwill coordinate\b.*\bworks\b/,
        /\bprofessional manner\b/,
        /\bcomprehensive solution\b/,
        /\bhigh[- ]quality\b/,
        /\bindustry[- ]leading\b/,
        /\bbest[- ]in[- ]class\b/,
        /\bworld[- ]class\b/,
        /\bcutting[- ]edge\b/,
        /\bseamless\b/,
        /\brobust\b/,
        /\bpeace of mind\b/,
        /\bcustomer satisfaction\b/,
    ];
    return genericPatterns.some(pattern => pattern.test(text));
}

/**
 * Has a human (or the Assistant acting for one) claimed this item's copy?
 *
 * The single definition, shared by the display resolver and the editable one so
 * the two cannot answer differently for the same item — which is exactly how
 * the editor came to show one paragraph while the printed quote showed another.
 */
export function isUserOwnedScopeCopy(item: ScopeCopyItem = {}): boolean {
    return item.manuallyEdited === true
        || item.editedBy === 'user'
        || item.editedBy === 'assistant'
        || !!item.humanEditedAt;
}

/**
 * 2026-08-19 (Peter, Camperdown 260846): a scope item description read "The
 * objective of this quote is to provide a price for moisture source
 * rectification..." — quote-preamble voice, not a work description. The
 * non-AI fallback scope path (WMS activities written straight into scope
 * during the outage) let the objective sentence through. Preamble voice is
 * rejected the same way AI filler is: never for human-owned copy, and the
 * resolver falls through to the next honest source.
 */
export function isObjectivePreambleVoice(value: unknown): boolean {
    const text = cleanText(value).toLowerCase();
    if (!text) return false;
    return [
        /^the (objective|purpose|aim|intent) of this (quote|quotation|proposal|document|works?)\b/,
        /^this (quote|quotation|proposal) (is|aims) to (provide|supply|give)\b/,
        /^we are pleased to (provide|submit|present)\b/,
        /^please find (below|attached|enclosed)\b/,
        /^the following quote\b/,
    ].some((pattern) => pattern.test(text));
}

// ─── The house Scope Description check ──────────────────────────────────────
// One sentence of about 12 to 30 words, never over 35 (HOUSE_DESCRIPTION_BUDGET
// in scope-writing-contract.js, measured on RJ Bird's sent quotes), naming the
// work: no finding or diagnosis, no process narration, no standard number and
// no product name. Pure: it reports, and the caller chooses another source or
// flags the row. It never rewrites text.

export type ClientScopeDescriptionReason =
    | 'empty'
    | 'sentences'
    | 'too-long'
    | 'finding'
    | 'narration'
    | 'standard-number'
    | 'product';

export interface ClientScopeDescriptionCheck {
    ok: boolean;
    reasons: ClientScopeDescriptionReason[];
    words: number;
    sentences: number;
}

export interface ClientScopeDescriptionCheckOptions {
    /** Product names from the job or the business catalogue. */
    products?: unknown[];
}

// "approx.", "e.g.", "No." and decimals do not end a sentence.
function sentenceText(value: unknown): string {
    return cleanText(value)
        .replace(/\bapprox\./gi, 'approx')
        .replace(/\b(?:e\.g|i\.e|etc|No)\./gi, 'x')
        .replace(/(\d)\.(\d)/g, '$1$2');
}

function sentencesOf(value: unknown): string[] {
    return sentenceText(value).split(/[.!?](?:\s+|$)/).map(part => part.trim()).filter(Boolean);
}

/** Sentences in a Scope Description, ignoring "approx.", "e.g.", "No." and decimals. */
export function countScopeSentences(value: unknown): number {
    const parts = sentencesOf(value);
    return sentenceText(value).trim() ? Math.max(1, parts.length) : 0;
}

/**
 * The first sentence, ending at a full stop that ends a sentence: "approx."
 * and "9.12" do not. Returns '' for empty text and the whole text when it has
 * no closing full stop.
 */
export function firstClientScopeSentence(value: unknown): string {
    const text = cleanText(value);
    if (!text) return '';
    const boundary = /[.!?](?=\s|$)/g;
    for (let match = boundary.exec(text); match; match = boundary.exec(text)) {
        const end = match.index + 1;
        if (/\bapprox\.$|\b(?:e\.g|i\.e|etc|No)\.$/i.test(text.slice(0, end))) continue;
        return text.slice(0, end).trim();
    }
    return text;
}

// A finding or diagnosis states what an inspection found, not the work.
const FINDING_OPENER = /^(?:(?:an?|the|our)\s+)?(?:(?:site|recent|visual|initial)\s+)?(?:inspection|investigation|assessment|testing|tests?|review|survey)\s+(?:has\s+|have\s+)?(?:confirmed|revealed|found|identified|showed|shows|indicated|indicates|noted|determined|established)\b|^it\s+(?:was|has\s+been|is)\s+(?:found|noted|observed|confirmed|identified|determined)\b/i;
const FINDING_WORDS = /\bconfined\s+(?:(?:entirely|solely|only)\s+)?to\b|\bdistress(?:ed)?\b/i;
// Process narration tells the order of work; the inclusion lines do that.
const NARRATION_OPENER = /^(?:(?:the\s+)?(?:remediation|rectification|repairs?|works?|scope|project)\s+(?:entails?|involves?|includes?|include|comprises?|consists?\s+of)|this\s+(?:scope|quote|item)\s+(?:covers|includes|involves))\b/i;
const NARRATION_WORDS = /\b(?:will|then|followed\s+by|entails?)\b/i;

export function checkClientScopeDescription(
    value: unknown,
    { products = [] }: ClientScopeDescriptionCheckOptions = {},
): ClientScopeDescriptionCheck {
    const text = cleanText(value);
    if (!text) return { ok: false, reasons: ['empty'], words: 0, sentences: 0 };
    const words = text.split(/\s+/).filter(Boolean).length;
    const sentences = countScopeSentences(text);
    const starts = sentencesOf(text);
    const reasons: ClientScopeDescriptionReason[] = [];
    if (sentences > HOUSE_DESCRIPTION_BUDGET.sentences) reasons.push('sentences');
    if (words > HOUSE_DESCRIPTION_BUDGET.maxWords) reasons.push('too-long');
    if (starts.some(sentence => FINDING_OPENER.test(sentence)) || FINDING_WORDS.test(text)) reasons.push('finding');
    if (starts.some(sentence => NARRATION_OPENER.test(sentence)) || NARRATION_WORDS.test(text)) reasons.push('narration');
    if (HOUSE_STANDARD_NUMBER.test(text)) reasons.push('standard-number');
    const lower = text.toLowerCase();
    const named = (Array.isArray(products) ? products : [])
        .map(product => String(product || '').trim().toLowerCase())
        .filter(Boolean);
    if (named.some(product => lower.includes(product))) reasons.push('product');
    return { ok: reasons.length === 0, reasons, words, sentences };
}

/** Why a generated Scope Description is held for builder review. */
export interface ClientScopeDescriptionReview {
    reasons: ClientScopeDescriptionReason[];
    words: number;
    sentences: number;
}

export interface ClientScopeDescriptionPick {
    /** The first candidate that passes, else the first non-empty one, else ''. */
    text: string;
    /** True when `text` passes the check and is neither filler nor preamble. */
    passed: boolean;
}

/** A candidate prints as written when it passes the check and is neither AI filler nor quote preamble. */
export function isPrintableClientScopeDescription(value: unknown, options: ClientScopeDescriptionCheckOptions = {}): boolean {
    return checkClientScopeDescription(value, options).ok
        && !isWeakClientScopeDescription(value)
        && !isObjectivePreambleVoice(value);
}

/**
 * Choose a generated Scope Description from candidate source fields, in the
 * caller's order. Nothing is rewritten: the first printable candidate wins;
 * when none passes, the first non-empty candidate is kept so the caller can
 * flag it for review.
 */
export function pickClientScopeDescription(
    candidates: unknown[],
    options: ClientScopeDescriptionCheckOptions = {},
): ClientScopeDescriptionPick {
    const texts = (Array.isArray(candidates) ? candidates : [])
        .map(candidate => cleanText(candidate))
        .filter(Boolean);
    const printable = texts.find(text => isPrintableClientScopeDescription(text, options));
    if (printable) return { text: printable, passed: true };
    return { text: texts[0] || '', passed: false };
}

/** The review receipt for a generated Scope Description, or null when it passes. */
export function clientScopeDescriptionReview(
    value: unknown,
    options: ClientScopeDescriptionCheckOptions = {},
): ClientScopeDescriptionReview | null {
    const check = checkClientScopeDescription(value, options);
    if (check.ok) return null;
    return { reasons: check.reasons, words: check.words, sentences: check.sentences };
}

export function resolveClientScopeDescription(item: ScopeCopyItem = {}, options: ResolveClientScopeOptions = {}): string {
    const fallback = options.fallback || {};
    const limit = finitePositiveLimit(options.limit);
    const fallbackToTitle = options.fallbackToTitle !== false;
    const userOwnedCopy = isUserOwnedScopeCopy(item);

    // If a human has manually edited this item's copy, and the client description
    // was explicitly cleared/emptied, their deliberate empty must stand — do NOT
    // fall through to legacy internal description or WMS fallback.
    if (userOwnedCopy && item.manuallyEdited === true) {
        const explicitFields = ['descriptionBrief', 'description_brief', 'clientDescription'];
        for (const field of explicitFields) {
            if (Object.prototype.hasOwnProperty.call(item, field)) {
                const raw = item[field] === null || item[field] === undefined ? '' : String(item[field]).trim();
                if (raw.length === 0) return '';
            }
        }
    }

    const primarySource =
        item.descriptionBrief
        || item.description_brief
        || item.clientDescription
        || (!userOwnedCopy ? item.notes_to_client : '')
        || (!userOwnedCopy ? (fallback.descriptionBrief
            || fallback.description_brief
            || fallback.clientDescription
            || fallback.notes_to_client) : '');
    const primary = cleanDisplayText(primarySource);
    // A human's own words are never "weak".
    //
    // isWeakClientScopeDescription exists to catch AI filler - "peace of mind",
    // "professional manner", "robust", "seamless". Applied to text the operator
    // typed himself it silently DISCARDS his edit and prints the first sentence
    // of the original description instead, or the item title. On a recovered
    // quote that original is the paragraph he was trying to replace, so the
    // change he made in the editor simply does not appear on the document he
    // sends. He has no way to see it happen and no way to defeat it: any
    // ordinary builder's sentence containing "robust" or "clean and tidy" is
    // reverted on the way to the page.
    //
    // The ownership flags are already stamped on every edit
    // (FullQuoteEditorModal stampEditedBy + manuallyEdited), so honouring them
    // here costs nothing and only ever makes a call site more faithful: no
    // caller wants a person's deliberate wording thrown away.
    if (primary && (userOwnedCopy
        || (!isWeakClientScopeDescription(primary) && !isObjectivePreambleVoice(primary)))) {
        return limitTextAtWordBoundary(primary, limit);
    }

    const detailed = cleanText(item.description || (!userOwnedCopy ? fallback.description : ''));
    if (detailed) {
        const sentence = firstSentence(detailed, limit);
        // Preamble voice in the detailed field falls through to the title —
        // unless a human owns this copy, in which case their words stand.
        if (userOwnedCopy || !isObjectivePreambleVoice(sentence)) return sentence;
    }

    if (fallbackToTitle) {
        // A description a human deliberately emptied stays empty.
        //
        // Reaching here with user-owned copy means every description field is
        // blank AND a person touched this item, which only happens when they
        // cleared the box on purpose. Printing the item's own title in the
        // description slot then repeats the title twice on the customer's page
        // — not what they asked for when they deleted the paragraph.
        //
        // Deliberately placed at the TITLE fallback and nowhere earlier: an
        // authored item with an empty brief but a real `description` returns
        // that description above and never arrives here, so this cannot blank a
        // quote whose copy simply lives in the other field.
        if (isUserOwnedScopeCopy(item)) return '';
        return firstSentence(item.title || fallback.title || '', limit) || primary;
    }
    return primary;
}

/** The four keys a scope item's Scope Description is stored under. */
// Type aliases, not interfaces: an interface has no implicit index signature,
// so the writers' results could not be passed where a ScopeCopyItem is taken.
export type ClientScopeWordingKeys = {
    description: string;
    descriptionBrief: string;
    description_brief: string;
    clientDescription: string;
};

/** Who wrote a copy edit, and when. One `now` stamps every field of one edit. */
export interface ScopeCopyWriteOptions {
    by?: string;
    now?: string;
}

/**
 * The same text under all four Scope Description keys.
 *
 * The quote prints `descriptionBrief` first (then `description_brief`, then
 * `clientDescription`), ~50 internal readers use `description`, and imported
 * items carry the text in all four. Writing one key leaves the others stale,
 * which is how an Assistant edit to `description` changed nothing the quote
 * showed (2026-09-25). Both manual editors already write all four.
 */
export function clientScopeWordingKeys(text: unknown): ClientScopeWordingKeys {
    const value = text === null || text === undefined ? '' : String(text);
    return { description: value, descriptionBrief: value, description_brief: value, clientDescription: value };
}

/** The builder-edit flags every copy writer sets. */
export type ScopeCopyEditStamps = {
    humanEditedAt: string;
    editedBy: string;
    manuallyEdited: true;
};

function copyEditStamps(by: string, now: string): ScopeCopyEditStamps {
    return { humanEditedAt: now, editedBy: by, manuallyEdited: true };
}

/**
 * Write a scope item's client-facing wording the way the two manual editors do
 * (Stage4ScopeBuilder updateItem, FullQuoteEditorModal updateScopeItem): all
 * four keys, the copy stamps that let the edit win the Stage 5 copy merge
 * (quote-scope-merge copyStampMs), and the builder-edit flags that make a
 * later scope morph leave it alone. Overwriting the internal `description` is
 * deliberate: an item has ONE Scope Description (operator decision
 * 2026-06-25, see backfillScopeDescription). An empty text is a deliberate
 * clear. Returns a new object; the input is not mutated.
 */
export function writeClientScopeWording<T extends object>(
    item: T,
    text: unknown,
    { by = 'assistant', now = new Date().toISOString() }: ScopeCopyWriteOptions = {},
): T & ClientScopeWordingKeys & ScopeCopyEditStamps & {
    copyEditedAt: string;
    descriptionBriefEditedAt: string;
    descriptionEditedAt: string;
} {
    return {
        ...item,
        ...clientScopeWordingKeys(text),
        copyEditedAt: now,
        descriptionBriefEditedAt: now,
        descriptionEditedAt: now,
        ...copyEditStamps(by, now),
    };
}

/**
 * A title or inclusions edit must not change the wording that prints.
 *
 * Both writers set `manuallyEdited`, and resolveClientScopeDescription then
 * reads any present-but-empty brief alias as a deliberate clear (the v6.0.54
 * rule). Items made by the old Assistant add path carry `descriptionBrief: ''`
 * beside a real `description`, so a title edit alone would blank their
 * printed wording. When that happens, the empty aliases take the wording
 * that printed before the edit.
 */
function keepPrintedWording<T extends object, U extends object>(before: T, after: U): U {
    const printedBefore = resolveClientScopeDescription(before as ScopeCopyItem, { fallbackToTitle: false });
    if (!printedBefore) return after;
    const printedAfter = resolveClientScopeDescription(after as ScopeCopyItem, { fallbackToTitle: false });
    if (printedAfter === printedBefore) return after;
    const record = after as Record<string, unknown>;
    const filled: Record<string, string> = {};
    for (const key of ['descriptionBrief', 'description_brief', 'clientDescription']) {
        if (Object.prototype.hasOwnProperty.call(record, key) && String(record[key] ?? '').trim() === '') {
            filled[key] = printedBefore;
        }
    }
    return { ...after, ...filled };
}

/** Set a scope item's title with the same builder copy-edit stamps. */
export function writeScopeItemTitle<T extends object>(
    item: T,
    title: unknown,
    { by = 'assistant', now = new Date().toISOString() }: ScopeCopyWriteOptions = {},
): T & ScopeCopyEditStamps & { title: string; titleEditedAt: string; copyEditedAt: string } {
    return keepPrintedWording(item, {
        ...item,
        title: title === null || title === undefined ? '' : String(title),
        titleEditedAt: now,
        copyEditedAt: now,
        ...copyEditStamps(by, now),
    });
}

/**
 * Set a scope item's inclusions with the same builder copy-edit stamps. The
 * caller passes the already merged list (mergeAiAuthoredInclusionsPreservingOperatorPricing).
 */
export function writeScopeItemInclusions<T extends object>(
    item: T,
    inclusions: unknown[],
    { by = 'assistant', now = new Date().toISOString() }: ScopeCopyWriteOptions = {},
): T & ScopeCopyEditStamps & { inclusions: unknown[]; inclusionsEditedAt: string; copyEditedAt: string } {
    return keepPrintedWording(item, {
        ...item,
        inclusions,
        inclusionsEditedAt: now,
        copyEditedAt: now,
        ...copyEditStamps(by, now),
    });
}

export function resolveEditableClientScopeDescription(item: ScopeCopyItem = {}, options: ResolveClientScopeOptions = {}): string {
    if (!item || typeof item !== 'object') return '';
    const userOwnedCopy = isUserOwnedScopeCopy(item);
    const explicitFields = ['descriptionBrief', 'description_brief', 'clientDescription', 'notes_to_client'];
    for (const field of explicitFields) {
        if (Object.prototype.hasOwnProperty.call(item, field)) {
            const value = item[field];
            const raw = value === null || value === undefined ? '' : String(value);
            if (raw.length > 0 && (raw.trim().length > 0 || userOwnedCopy)) return raw;
            if (userOwnedCopy) return '';
        }
    }
    return resolveClientScopeDescription(item, options);
}

/**
 * Backfill a scope item's legacy `description` field from its single Scope
 * Description when the field is empty.
 *
 * QuoteMaker scope items historically carried TWO descriptions: the
 * client-facing Scope Description (`descriptionBrief` / `description_brief`) and
 * a separate longer "internal / detailed work description" (`description`) the
 * AI authored for the builder. Operator decision (Peter 2026-06-25): a scope
 * item has exactly ONE Scope Description — the one the client sees — and the
 * internal variant is gone. The AI prompts no longer author a separate
 * `description`, and the Stage 5 editor no longer shows it.
 *
 * But ~50 internal readers (costing, work-method, learning, gold-standard,
 * photo auto-assign, semantic tagging) still read `item.description`. To keep
 * them working without a 50-site rewrite, this mirrors the Scope Description
 * into the legacy `description` field WHENEVER `description` is empty (i.e. the
 * AI didn't author one). It is the compatibility shim beneath the
 * single-Scope-Description design.
 *
 * Contract:
 *  - FILL-IF-EMPTY — only populates `description` when it is empty/absent. An
 *    item that already carries a `description` (legacy data, a manual edit, a
 *    prompt override) is returned UNTOUCHED, so nothing is silently rewritten.
 *  - PURE — returns a NEW object only when it fills; otherwise the same
 *    reference, so referential-equality memo gates are not disturbed.
 *  - NEVER blanks — if no Scope Description can be resolved, returns the item
 *    untouched.
 */
export function backfillScopeDescription<T extends ScopeCopyItem>(item: T): T {
    if (!item || typeof item !== 'object') return item;
    const existing = typeof item.description === 'string' ? item.description.trim() : '';
    if (existing.length > 0) return item;
    // Mirror the explicit Scope Description only — never invent one from the
    // title (fallbackToTitle:false). A degenerate item with no Scope
    // Description keeps an empty `description`.
    const client = resolveClientScopeDescription(item, { fallbackToTitle: false });
    if (typeof client !== 'string' || client.trim().length === 0) return item;
    return { ...item, description: client };
}
